/**
 * StockCountPage — ספירת מלאי
 *
 * 3-step wizard:
 *   Step 1: Choose warehouse + count date
 *   Step 2: Enter physical quantities for each product
 *   Step 3: Review adjustments → confirm and save
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardList, ChevronRight, ChevronLeft, Save, Loader as Loader2, Search, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, History, Calendar, Warehouse, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useTableSort, sortRows } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/ui/SortableTh';
import { useCompany } from '@/context/CompanyContext';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WarehouseOption { id: string; name: string; }

interface ProductRow {
  product_id:      string;
  sku:             string | null;
  name:            string;
  system_quantity: number;
  counted_quantity: number | null;
  touched:         boolean;
}

interface CountHistoryRow {
  id:              string;
  count_date:      string;
  warehouse_name:  string;
  notes:           string | null;
  created_at:      string;
  line_count:      number;
  total_adjustment: number;
}

type Step = 'setup' | 'count' | 'review';
type Tab = 'new' | 'history';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function twoMonthsAgo(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 2);
  return d.toISOString().slice(0, 10);
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function StockCountPage() {
  const { activeCompanyId } = useCompany();

  const [tab, setTab]     = useState<Tab>('new');

  // Step 1 state
  const [step, setStep]               = useState<Step>('setup');
  const [warehouses, setWarehouses]   = useState<WarehouseOption[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [countDate, setCountDate]     = useState(today());
  const [notes, setNotes]             = useState('');

  // Step 2 state
  const [products, setProducts]       = useState<ProductRow[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [search, setSearch]           = useState('');
  const [showOnlyChanged, setShowOnlyChanged] = useState(false);

  // Step 3 / submit
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // History
  const [history, setHistory]         = useState<CountHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedCount, setExpandedCount]   = useState<string | null>(null);
  const [expandedLines, setExpandedLines]   = useState<Array<{
    product_name: string; sku: string | null; system_quantity: number;
    counted_quantity: number; adjustment: number;
  }>>([]);

  // ── Load warehouses ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeCompanyId) return;
    supabase.from('inventory_warehouses')
      .select('id, name')
      .eq('company_id', activeCompanyId)
      .eq('is_external', false)
      .order('name')
      .then(({ data }) => {
        if (data) setWarehouses(data);
      });
  }, [activeCompanyId]);

  // ── Load ALL products + merge balances for selected warehouse ───────────────
  const loadProducts = useCallback(async () => {
    if (!activeCompanyId || !warehouseId) return;
    setLoadingProducts(true);

    const [prodRes, balRes] = await Promise.all([
      supabase
        .from('inventory_products')
        .select('id, sku, name')
        .eq('company_id', activeCompanyId)
        .eq('is_active', true)
        .order('name'),
      supabase.rpc('get_inventory_balances_as_of', {
        p_company_id:       activeCompanyId,
        p_as_of_date:       countDate,
        p_valuation_method: 'CURRENT_COST',
        p_warehouse_id:     warehouseId,
      }).limit(10000),
    ]);

    if (prodRes.error) {
      console.error('Error loading products:', prodRes.error);
      setLoadingProducts(false);
      return;
    }

    const balanceMap = new Map<string, number>();
    if (balRes.data) {
      for (const r of balRes.data as Array<{ product_id: string; warehouse_id: string; calculated_quantity: number }>) {
        if (r.warehouse_id === warehouseId) {
          balanceMap.set(r.product_id, Number(r.calculated_quantity));
        }
      }
    }

    const rows: ProductRow[] = (prodRes.data ?? []).map(p => ({
      product_id:       p.id,
      sku:              p.sku,
      name:             p.name,
      system_quantity:  balanceMap.get(p.id) ?? 0,
      counted_quantity: null,
      touched:          false,
    }));

    setProducts(rows);
    setLoadingProducts(false);
  }, [activeCompanyId, warehouseId, countDate]);

  // ── Load history ───────────────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    if (!activeCompanyId) return;
    setHistoryLoading(true);

    const { data, error } = await supabase
      .from('stock_counts')
      .select(`
        id, count_date, notes, created_at, warehouse_id,
        inventory_warehouses ( name ),
        stock_count_lines ( adjustment )
      `)
      .eq('company_id', activeCompanyId)
      .not('warehouse_id', 'is', null)
      .order('count_date', { ascending: false })
      .limit(50);

    if (error) {
      console.error('History load error:', error);
      setHistoryLoading(false);
      return;
    }

    const mapped: CountHistoryRow[] = (data ?? []).map((r: Record<string, unknown>) => {
      const lines = (r.stock_count_lines ?? []) as Array<{ adjustment: number }>;
      const wh = r.inventory_warehouses as { name: string } | null;
      return {
        id:               r.id as string,
        count_date:       r.count_date as string,
        warehouse_name:   wh?.name ?? '—',
        notes:            r.notes as string | null,
        created_at:       r.created_at as string,
        line_count:       lines.length,
        total_adjustment: lines.reduce((s, l) => s + Number(l.adjustment), 0),
      };
    });

    setHistory(mapped);
    setHistoryLoading(false);
  }, [activeCompanyId]);

  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  // ── Expand a history row to see line details ───────────────────────────────
  const toggleExpand = async (countId: string) => {
    if (expandedCount === countId) { setExpandedCount(null); return; }
    setExpandedCount(countId);

    const { data } = await supabase
      .from('stock_count_lines')
      .select('product_id, system_quantity, counted_quantity, adjustment, inventory_products ( name, sku )')
      .eq('stock_count_id', countId);

    setExpandedLines((data ?? []).map((l: Record<string, unknown>) => {
      const prod = l.inventory_products as { name: string; sku: string | null } | null;
      return {
        product_name:    prod?.name ?? '—',
        sku:             prod?.sku ?? null,
        system_quantity: Number(l.system_quantity),
        counted_quantity: Number(l.counted_quantity),
        adjustment:      Number(l.adjustment),
      };
    }));
  };

  const { sort: sort1, toggle: toggle1 } = useTableSort<string>('name', 'asc');
  const { sort: sort2, toggle: toggle2 } = useTableSort<string>('name', 'asc');
  const { sort: sort3, toggle: toggle3 } = useTableSort<string>('product_name', 'asc');

  // ── Computed: lines that changed ───────────────────────────────────────────
  const changedLines = useMemo(() => {
    const base = products.filter(p => p.touched && p.counted_quantity !== null && p.counted_quantity !== p.system_quantity);
    if (sort2.key === 'diff') {
      return sortRows(base.map(p => ({ ...p, _diff: (p.counted_quantity ?? 0) - p.system_quantity })), '_diff', sort2.dir).map(({ _diff, ...rest }) => rest as typeof base[number]);
    }
    return sortRows(base, sort2.key, sort2.dir);
  }, [products, sort2]);

  const touchedLines = useMemo(() =>
    products.filter(p => p.touched && p.counted_quantity !== null),
    [products]
  );

  // ── Filtered products for step 2 ──────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    let list = products;
    if (showOnlyChanged) list = list.filter(p => p.touched);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(p =>
      p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q)
    );
    if (sort1.key === 'diff') {
      return sortRows(list.map(p => ({ ...p, _diff: (p.counted_quantity ?? p.system_quantity) - p.system_quantity })), '_diff', sort1.dir).map(({ _diff, ...rest }) => rest as typeof list[number]);
    }
    return sortRows(list, sort1.key, sort1.dir);
  }, [products, search, showOnlyChanged, sort1]);

  // ── Update a product's counted quantity ────────────────────────────────────
  const setCountedQty = (productId: string, value: string) => {
    setProducts(prev => prev.map(p =>
      p.product_id === productId
        ? { ...p, counted_quantity: value === '' ? null : Number(value), touched: value !== '' }
        : p
    ));
  };

  // ── Navigate between steps ─────────────────────────────────────────────────
  const goToCount = () => {
    loadProducts();
    setStep('count');
  };

  const goToReview = () => setStep('review');
  const goBackToCount = () => setStep('count');
  const goBackToSetup = () => { setStep('setup'); setProducts([]); };

  // ── Submit the stock count ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!activeCompanyId) return;
    setSaving(true);
    setSaveError(null);

    const lines = touchedLines.map(p => ({
      product_id:       p.product_id,
      system_quantity:  p.system_quantity,
      counted_quantity: p.counted_quantity ?? p.system_quantity,
    }));

    const { error } = await supabase.rpc('apply_stock_count', {
      p_company_id:  activeCompanyId,
      p_warehouse_id: warehouseId,
      p_count_date:   countDate,
      p_notes:        notes || null,
      p_lines:        lines,
    });

    setSaving(false);

    if (error) {
      setSaveError(error.message);
      return;
    }

    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setStep('setup');
      setProducts([]);
      setWarehouseId('');
      setCountDate(today());
      setNotes('');
    }, 2000);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  const warehouseName = warehouses.find(w => w.id === warehouseId)?.name ?? '';

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-50 rounded-lg">
            <ClipboardList className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">ספירת מלאי</h1>
            <p className="text-sm text-gray-500">ספירה פיזית והתאמת יתרות</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('new')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> ספירה חדשה
          </span>
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <span className="flex items-center gap-2">
            <History className="w-4 h-4" /> היסטוריית ספירות
          </span>
        </button>
      </div>

      {/* ── New Count Wizard ────────────────────────────────────────────────── */}
      {tab === 'new' && (
        <>
          {/* Progress bar */}
          <div className="flex items-center gap-2 text-sm">
            {(['setup', 'count', 'review'] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                {i > 0 && <ChevronLeft className="w-4 h-4 text-gray-300" />}
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  step === s
                    ? 'bg-amber-100 text-amber-800'
                    : products.length > 0 && i < ['setup', 'count', 'review'].indexOf(step)
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-500'
                }`}>
                  {s === 'setup' && 'בחירת מחסן'}
                  {s === 'count' && 'הזנת כמויות'}
                  {s === 'review' && 'אישור ושמירה'}
                </span>
              </div>
            ))}
          </div>

          {/* ── Step 1: Setup ────────────────────────────────────────────── */}
          {step === 'setup' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Warehouse className="w-5 h-5 text-amber-500" />
                פרטי הספירה
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">מחסן</label>
                  <select
                    value={warehouseId}
                    onChange={e => setWarehouseId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  >
                    <option value="">בחר מחסן...</option>
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> תאריך הספירה
                    </span>
                  </label>
                  <input
                    type="date"
                    value={countDate}
                    min={twoMonthsAgo()}
                    max={today()}
                    onChange={e => setCountDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">ניתן להזין עד חודשיים אחורה</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">הערות (לא חובה)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="ספירה שנתית, ספירה חלקית..."
                />
              </div>

              <div className="flex justify-start">
                <button
                  disabled={!warehouseId || !countDate}
                  onClick={goToCount}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  המשך להזנת כמויות
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Count ────────────────────────────────────────────── */}
          {step === 'count' && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>{warehouseName}</strong> — ספירה לתאריך{' '}
                <strong>{new Date(countDate).toLocaleDateString('he-IL')}</strong>
                {' '}·{' '}
                {products.length} מוצרים
                {' '}·{' '}
                {touchedLines.length} נספרו
              </div>

              {/* Search + filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="חיפוש לפי שם או מק״ט..."
                    className="w-full pr-10 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showOnlyChanged}
                    onChange={e => setShowOnlyChanged(e.target.checked)}
                    className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  הצג רק שנספרו
                </label>
              </div>

              {/* Products table */}
              {loadingProducts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                          <SortableTh label="מוצר" sortKey="name" activeKey={sort1.key} dir={sort1.dir} onSort={toggle1} />
                          <SortableTh label="מק״ט" sortKey="sku" activeKey={sort1.key} dir={sort1.dir} onSort={toggle1} className="w-24" />
                          <SortableTh label="יתרה במערכת" sortKey="system_quantity" activeKey={sort1.key} dir={sort1.dir} onSort={toggle1} align="center" className="w-28" />
                          <SortableTh label="כמות נספרה" sortKey="counted_quantity" activeKey={sort1.key} dir={sort1.dir} onSort={toggle1} align="center" className="w-36" />
                          <SortableTh label="הפרש" sortKey="diff" activeKey={sort1.key} dir={sort1.dir} onSort={toggle1} align="center" className="w-24" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredProducts.map(p => {
                          const diff = p.counted_quantity !== null ? p.counted_quantity - p.system_quantity : null;
                          return (
                            <tr key={p.product_id} className={`hover:bg-gray-50 ${
                              diff !== null && diff !== 0 ? 'bg-amber-50/40' : ''
                            }`}>
                              <td className="px-4 py-3 font-medium text-gray-900">{p.name}</td>
                              <td className="px-4 py-3 text-gray-500 text-xs">{p.sku ?? '—'}</td>
                              <td className="px-4 py-3 text-center text-gray-700 font-mono">{p.system_quantity}</td>
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={p.counted_quantity ?? ''}
                                  onChange={e => setCountedQty(p.product_id, e.target.value)}
                                  placeholder="—"
                                  className="w-24 text-center border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono"
                                />
                              </td>
                              <td className="px-4 py-3 text-center font-mono text-sm">
                                {diff === null ? (
                                  <span className="text-gray-300">—</span>
                                ) : diff === 0 ? (
                                  <span className="text-green-600">0</span>
                                ) : diff > 0 ? (
                                  <span className="text-blue-600">+{diff}</span>
                                ) : (
                                  <span className="text-red-600">{diff}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {filteredProducts.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                              {products.length === 0 ? 'אין מוצרים פעילים' : 'אין תוצאות'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={goBackToSetup}
                  className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                  חזרה
                </button>
                <button
                  disabled={touchedLines.length === 0}
                  onClick={goToReview}
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  סיכום ואישור
                  <ChevronLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Review ───────────────────────────────────────────── */}
          {step === 'review' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
                <h2 className="text-lg font-semibold text-gray-800">סיכום ספירת מלאי</h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">מחסן</div>
                    <div className="font-semibold text-gray-900">{warehouseName}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">תאריך ספירה</div>
                    <div className="font-semibold text-gray-900">{new Date(countDate).toLocaleDateString('he-IL')}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">מוצרים שנספרו</div>
                    <div className="font-semibold text-gray-900">{touchedLines.length}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-gray-500">הפרשים</div>
                    <div className={`font-semibold ${changedLines.length > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      {changedLines.length}
                    </div>
                  </div>
                </div>

                {changedLines.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      מוצרים עם הפרש (יווצרו תנועות התאמה)
                    </h3>
                    <div className="overflow-x-auto rounded-lg border border-amber-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-amber-50 text-xs text-amber-800 uppercase">
                            <SortableTh label="מוצר" sortKey="name" activeKey={sort2.key} dir={sort2.dir} onSort={toggle2} />
                            <SortableTh label="במערכת" sortKey="system_quantity" activeKey={sort2.key} dir={sort2.dir} onSort={toggle2} align="center" />
                            <SortableTh label="נספר" sortKey="counted_quantity" activeKey={sort2.key} dir={sort2.dir} onSort={toggle2} align="center" />
                            <SortableTh label="הפרש" sortKey="diff" activeKey={sort2.key} dir={sort2.dir} onSort={toggle2} align="center" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {changedLines.map(p => {
                            const diff = (p.counted_quantity ?? 0) - p.system_quantity;
                            return (
                              <tr key={p.product_id}>
                                <td className="px-4 py-2 text-gray-900">{p.name} <span className="text-xs text-gray-400">{p.sku ?? ''}</span></td>
                                <td className="px-4 py-2 text-center font-mono">{p.system_quantity}</td>
                                <td className="px-4 py-2 text-center font-mono font-semibold">{p.counted_quantity}</td>
                                <td className={`px-4 py-2 text-center font-mono font-semibold ${diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  {diff > 0 ? `+${diff}` : diff}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {touchedLines.length > 0 && changedLines.length === 0 && (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <CheckCircle2 className="w-5 h-5" />
                    כל המוצרים שנספרו תואמים ליתרה במערכת — לא יווצרו הפרשים.
                  </div>
                )}

                {saveError && (
                  <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                    שגיאה בשמירה: {saveError}
                  </div>
                )}

                {saveSuccess && (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <CheckCircle2 className="w-5 h-5" />
                    הספירה נשמרה בהצלחה!
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center">
                <button
                  onClick={goBackToCount}
                  className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                  חזרה לעריכה
                </button>
                <button
                  disabled={saving || saveSuccess || touchedLines.length === 0}
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  אשר ושמור ספירה
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── History Tab ─────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              עדיין לא בוצעו ספירות מלאי
            </div>
          ) : (
            <div className="divide-y">
              {history.map(h => (
                <div key={h.id}>
                  <button
                    onClick={() => toggleExpand(h.id)}
                    className="w-full text-right px-5 py-4 hover:bg-gray-50 flex items-center gap-4 transition-colors"
                  >
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                      <div>
                        <span className="text-xs text-gray-400">תאריך ספירה</span>
                        <div className="font-medium">{new Date(h.count_date).toLocaleDateString('he-IL')}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">מחסן</span>
                        <div className="font-medium">{h.warehouse_name}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">מוצרים</span>
                        <div className="font-medium">{h.line_count}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">סה״כ הפרש</span>
                        <div className={`font-medium font-mono ${
                          h.total_adjustment === 0 ? 'text-green-600'
                            : h.total_adjustment > 0 ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {h.total_adjustment > 0 ? `+${h.total_adjustment}` : h.total_adjustment}
                        </div>
                      </div>
                      <div className="hidden md:block">
                        <span className="text-xs text-gray-400">הערות</span>
                        <div className="text-gray-600 truncate">{h.notes ?? '—'}</div>
                      </div>
                    </div>
                    {expandedCount === h.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>

                  {expandedCount === h.id && (
                    <div className="px-5 pb-4">
                      <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                            <SortableTh label="מוצר" sortKey="product_name" activeKey={sort3.key} dir={sort3.dir} onSort={toggle3} />
                            <SortableTh label="במערכת" sortKey="system_quantity" activeKey={sort3.key} dir={sort3.dir} onSort={toggle3} align="center" />
                            <SortableTh label="נספר" sortKey="counted_quantity" activeKey={sort3.key} dir={sort3.dir} onSort={toggle3} align="center" />
                            <SortableTh label="הפרש" sortKey="adjustment" activeKey={sort3.key} dir={sort3.dir} onSort={toggle3} align="center" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {sortRows(expandedLines, sort3.key, sort3.dir).map((l, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2">{l.product_name} <span className="text-xs text-gray-400">{l.sku ?? ''}</span></td>
                              <td className="px-3 py-2 text-center font-mono">{l.system_quantity}</td>
                              <td className="px-3 py-2 text-center font-mono">{l.counted_quantity}</td>
                              <td className={`px-3 py-2 text-center font-mono ${
                                l.adjustment > 0 ? 'text-blue-600' : l.adjustment < 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {l.adjustment > 0 ? `+${l.adjustment}` : l.adjustment}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
